import { useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOrganisationData } from "../hooks/useOrganisationData";
import { ACCESS_LEVELS } from "../constants/accessLevels";
import styles from "./PublicOrganisationDetail.module.scss";
import {
  Button,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { GlobeIcon } from "../components/Icons/Globe";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import classNames from "classnames";
import { AuthContext } from "../context/AuthContext";
import { useEnumLabels } from "../hooks/useEnumLabels";

export const PublicOrganisationDetail = () => {
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const { data: organisation, loading, error, accessLevel } = useOrganisationData(organisationId!);
  const { getInterestLabelById, getEventTypeLabelById } = useEnumLabels();
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;

  if (loading) {
    return (
      <div className={classNames(styles.container, styles.centered)}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classNames(styles.container, styles.centered)}>
        <Alert severity="error">
          {error}
          {!isAuthenticated && " Pro přístup k dalším informacím se prosím přihlaste."}
        </Alert>
      </div>
    );
  }

  if (!organisation) {
    return (
      <div className={classNames(styles.container, styles.centered)}>
        <Alert severity="warning">Organizace nebyla nalezena</Alert>
      </div>
    );
  }

  const orgData = organisation as any;
  const logo = orgData.logo;
  const name = orgData.name || orgData.organisation_name;
  const email = orgData.email;
  const phoneNumber = orgData.phone_number;
  const address = orgData.address;
  const organisationInfo = orgData.organisation_info;
  const websiteLink = orgData.website_link;
  const areaOfInterest = orgData.area_of_interest || orgData.focus_areas;
  const eventType = orgData.event_type;
  const contactPersonName = orgData.contact_person_name || orgData.contact_person;
  const contactPersonPhone = orgData.contact_person_phone;
  const contactPersonEmail = orgData.contact_person_email;

  return (
    <div className={styles.container}>
      {accessLevel === ACCESS_LEVELS.PUBLIC && (
        <div className={styles.publicBadge}>
          <Chip label="Veřejný profil organizace" size="small" />
        </div>
      )}

      <div className={styles.header}>
        {logo && (
          <div className={styles.logoWrapper}>
            <img
              src={logo}
              alt={name}
              className={styles.logo}
            />
          </div>
        )}
        <div className={styles.headerInfo}>
          <h1>{name}</h1>
          {email && (
            <div className={styles.contactItem}>
              <EmailIcon />
              <span>{email}</span>
            </div>
          )}
          {phoneNumber && (
            <div className={styles.contactItem}>
              <PhoneIcon />
              <span>{phoneNumber}</span>
            </div>
          )}
          {address && (
            <div className={styles.contactItem}>
              <LocationOnIcon />
              <span>{address}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.content}>
        {organisationInfo && (
          <section className={styles.section}>
            <h2>O organizaci</h2>
            <p className={styles.text}>{organisationInfo}</p>
          </section>
        )}

        {websiteLink && (
          <section className={styles.section}>
            <h2>Webové stránky</h2>
            <a
              href={`https://${websiteLink}`}
              target="_blank"
              rel="noreferrer"
              className={styles.websiteLink}
            >
              <GlobeIcon />
              {websiteLink}
            </a>
          </section>
        )}

        {areaOfInterest && areaOfInterest.length > 0 && (
          <section className={styles.section}>
            <h2>Oblasti zájmu</h2>
            <div className={styles.chips}>
              {areaOfInterest.map((interestId: number, idx: number) => (
                <Chip
                  key={idx}
                  label={getInterestLabelById(interestId)}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </div>
          </section>
        )}

        {eventType && eventType.length > 0 && (
          <section className={styles.section}>
            <h2>Typy akcí</h2>
            <div className={styles.chips}>
              {eventType.map((typeId: number, idx: number) => (
                <Chip
                  key={idx}
                  label={getEventTypeLabelById(typeId) || typeId}
                  variant="outlined"
                  color="secondary"
                />
              ))}
            </div>
          </section>
        )}

        {contactPersonName && (
          <section className={styles.section}>
            <h2>Kontaktní osoba</h2>
            <p className={styles.text}>
              <strong>{contactPersonName}</strong>
            </p>
            {contactPersonPhone && (
              <div className={styles.contactItem}>
                <PhoneIcon />
                <span>{contactPersonPhone}</span>
              </div>
            )}
            {contactPersonEmail && (
              <div className={styles.contactItem}>
                <EmailIcon />
                <span>{contactPersonEmail}</span>
              </div>
            )}
          </section>
        )}
      </div>

      {accessLevel === ACCESS_LEVELS.AUTHENTICATED && (
        <div className={styles.footer}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            Zpět
          </Button>
        </div>
      )}
    </div>
  );
};

